import { c as _c } from "react/compiler-runtime";
import { Box } from '@chakra-ui/react';
import { AdvisorsIllustration } from '@src/components/sco/Advisors/AdvisorsLandingIllustrations';
import { FundingIllustration } from '@src/components/sco/Funding/FundingLandingIllustrations';
import { GenericContentBlockConfig, GenericContentBlocks } from '@src/components/sco/GenericLP/GenericContentBlock';
import GenericHero from '@src/components/sco/GenericLP/GenericHero';
import GenericImage from '@src/components/sco/GenericLP/GenericImage';
import GenericSection from '@src/components/sco/GenericLP/GenericSection';
import GenericStats, { GenericStatItem } from '@src/components/sco/GenericLP/GenericStats';
import { HomePaths } from '@src/components/sco/Home/HomePaths';
import { MasonryGridBlock } from '@src/components/sco/InvestorMatching/InvestorMatchingLanding/blocks/MasonryGridBlock';
import { UserGroupRequestGoalsList } from '@src/components/sco/Requests/UserGroupRequestGoalsList';
import { SignupRequestChatIntake } from '@src/components/sco/SignupRequestChatIntake';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { useActiveContentContext } from '@src/contexts/ActiveContentContext';
import { useUniversalEffect } from '@src/hooks/useUniversalEffect.tsx';
import { TrackEvent } from '@src/lib/TrackEvent.tsx';
import React from 'react';
import { LuCircleDollarSign, LuHandshake, LuRocket, LuUsers } from 'react-icons/lu';
export { getServerSideProps } from '@src/lib/server-side-rendering.tsx';
const variantKey = 'customers';
const SECTION_BG = 'colorPalette.bg';

// ─── Page content (edit here) ─────────────────────────────────────────────────

const stats: GenericStatItem[] = [{
  icon: LuRocket,
  text: 'Over 1 Million Startups'
}, {
  icon: LuCircleDollarSign,
  text: '$20B Raised by our Startups'
}, {
  icon: LuHandshake,
  text: '$1B+ Capital Commitments'
}, {
  icon: LuRocket,
  text: '15 IPOs'
}, {
  icon: LuUsers,
  text: '15+ Years of Service'
}];
const matchingBlocks: GenericContentBlockConfig[] = [{
  heading: 'Decks that get funded.',
  paragraph: 'Upload what you have and get an honest read on where it loses investors. We rewrite the weak slides with you, so the story lands before you walk in the room.',
  listItems: ['Your deck scored against what investors fund', 'The exact slides costing you the meeting', 'Rewrites you can use the same day'],
  primaryCta: {
    label: 'Audit my Deck',
    href: '/pitch-decks'
  },
  visual: <GenericImage src={'/assets/illustration/pitchdecks/pitch-deck-2-grade.svg'} />
}, {
  heading: 'Investors who write checks.',
  paragraph: 'You need capital yesterday. Skip the line and talk to investors who match exactly your funding requirements. We’ll guide you at every step, from initial pitch to negotiating your term sheet.',
  listItems: ['Matched investors, ranked by fit', 'Outreach emails drafted for each one', 'Your whole pipeline tracked in one place'],
  primaryCta: {
    label: 'Find Investors',
    href: '/investors/matching/start'
  },
  visual: <FundingIllustration illustrationIndex={0} />
}, {
  heading: 'Advisors who’ve done it.',
  paragraph: 'Every founder hits problems someone else has solved. Tell us where you need help and we match you to advisors worth the time, on terms you set.',
  listItems: ['Answer your biggest questions immediately', 'Access to world class Advisors', 'Get warm intros to who you need to meet'],
  // L11: Meet Advisors opens the public directory; Become an Advisor is the supply-side CTA.
  primaryCta: {
    label: 'Meet Advisors',
    href: '/advisors/directory'
  },
  secondaryCta: {
    label: 'Become an Advisor',
    href: '/advisors/become-a-startup-advisor'
  },
  visual: <AdvisorsIllustration illustrationIndex={6} />
}
// {
//   heading: 'Find your first customers.',
//   paragraph:
//     'We’ll help you find out how to acquire your first customers. We’ll map where they are, how to sell to them, and what we can do right now to test their interest.',
//   listItems: [
//     'A detailed map of where to find your customers',
//     'Proven tests to drive interest and conversions',
//     'Cost effective acquisition for near zero budgets',
//   ],
//   primaryCta: { label: 'Find My Customers', href: '/investors/matching/start' },
//   visual: <CustomersIllustration illustrationIndex={1} />,
// },
];
const faqItems = [{
  question: 'Where do I even start?',
  answer: 'An Investor Grade is a score out of 100 that represents how well your deck is prepared for an investor pitch. It is based on a variety of factors, including the quality of your deck, the strength of your pitch, and the likelihood that your deck will be funded.'
}, {
  question: 'How do you know who my customers are?',
  answer: 'We score your deck based on a variety of factors, including the quality of your deck, the strength of your pitch, and the likelihood that your deck will be funded.'
}, {
  question: 'Do you write my outreach for me?',
  answer: 'No, you can research without one, but you’ll want a quality deck before outreach.'
}, {
  question: "What if I don't have any customers yet?",
  answer: 'We’ll send personalized emails to your matched investors on your behalf, tracking responses and follow-ups.'
}, {
  question: 'Is this just for a certain type of business?',
  answer: 'It’s faster, more personalized, and eliminates the need to write and send each email manually.'
}, {
  question: 'Is it free to get started?',
  answer: 'Yes, but it depends on the investor and your relationship. Some will respond, some won’t.'
}];

// ─── Page ─────────────────────────────────────────────────────────────────────

export const HomeLandingSection = () => {
  const $ = _c(38);
  const activeContent = useActiveContentContext();
  const {
    getBreakpointValue
  } = useBreakpointContext();
  let t0;
  if ($[0] !== activeContent) {
    t0 = () => {
      activeContent.setItem({
        __typename: "LandingPage",
        title: "Startup Growth Engine",
        description: "Grow like your team just got 10x bigger. Pitch decks, investor matching, and advisors in one platform \u2014 so you can launch and scale without giving up equity."
      });
      return () => activeContent.setItem(null);
    };
    $[0] = activeContent;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  useUniversalEffect(t0, t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <TrackEvent action="lpViewed" onlyOnScreen={true} traits={{
      variantKey
    }} />;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== getBreakpointValue) {
    t3 = getBreakpointValue([6, 8, 16, 20]);
    $[4] = getBreakpointValue;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== getBreakpointValue) {
    t4 = getBreakpointValue([6, 8, 16, 20]);
    $[6] = getBreakpointValue;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== getBreakpointValue) {
    t5 = getBreakpointValue([0, 0, 8]);
    $[8] = getBreakpointValue;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t3 || $[11] !== t4 || $[12] !== t5) {
    t6 = {
      paddingBottom: t3,
      paddingTop: t4,
      paddingX: t5
    };
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== getBreakpointValue) {
    t7 = getBreakpointValue([3, 4, 0]);
    $[14] = getBreakpointValue;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t7) {
    t8 = {
      paddingX: t7
    };
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== getBreakpointValue) {
    t9 = getBreakpointValue([3, 4, 0]);
    $[18] = getBreakpointValue;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t9) {
    t10 = {
      paddingX: t9
    };
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <UserGroupRequestGoalsList compact={true} ctaLabel="Continue" />;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== getBreakpointValue) {
    t12 = getBreakpointValue([true, true, false]);
    $[23] = getBreakpointValue;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t12) {
    t13 = <HomePaths>{t11}<SignupRequestChatIntake funnelIdentifier="home-landing-wil" hideFileAttach={t12} /></HomePaths>;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t10 || $[28] !== t13 || $[29] !== t6 || $[30] !== t8) {
    t14 = <GenericHero gradientText="Startup Growth Engine" heading="Startup Growth Engine" containerProps={t6} headingProps={t8} subheadingProps={t10}>{t13}</GenericHero>;
    $[27] = t10;
    $[28] = t13;
    $[29] = t6;
    $[30] = t8;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <GenericStats background="colorPalette.container/50" heading="Our Track Record" stats={stats} />;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  let t17;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <GenericSection background={SECTION_BG} heading="No more waiting for Growth" subheading="Connected apps that handle the hardest parts of building a startup."><GenericContentBlocks blocks={matchingBlocks} /></GenericSection>;
    t17 = <MasonryGridBlock background="colorPalette.panel" />;
    $[33] = t16;
    $[34] = t17;
  } else {
    t16 = $[33];
    t17 = $[34];
  }
  let t18;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <GenericSection background={SECTION_BG} gradientText="10x Bigger" heading="Grow like your Team just got 10x Bigger"><HomePaths /></GenericSection>;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== t14) {
    t19 = <Box color="fg" width="full">{t2}{t14}{t15}{t16}{t17}{t18}</Box>;
    $[36] = t14;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  return t19;
};